import type { CSSProperties } from "react";

/** What a job or invoice status means, which decides its colour. */
export enum StatusTone {
  NEUTRAL = "neutral",
  ACTIVE = "active",
  DONE = "done",
  // Off the normal path (withdrawn, part-paid), not an error: don't reuse it for error states.
  EXCEPTION = "exception",
}

// Solid fills, white text at 4.5:1 or more; flat so a status never looks like a button.
export const FLAT_TONE_COLORS: Record<StatusTone, CSSProperties> = {
  [StatusTone.NEUTRAL]: { backgroundColor: "#64748b", color: "#ffffff" },
  [StatusTone.ACTIVE]: { backgroundColor: "#2563eb", color: "#ffffff" },
  [StatusTone.DONE]: { backgroundColor: "#15803d", color: "#ffffff" },
  [StatusTone.EXCEPTION]: { backgroundColor: "#991b1b", color: "#ffffff" },
};

// The saturated fills the glossy (raised) pill used, kept in case design switches back.
export const GLOSSY_TONE_COLORS: Record<StatusTone, CSSProperties> = {
  [StatusTone.NEUTRAL]: { backgroundColor: "#64748b", color: "#ffffff" },
  [StatusTone.ACTIVE]: { backgroundColor: "#2563eb", color: "#ffffff" },
  [StatusTone.DONE]: { backgroundColor: "#15803d", color: "#ffffff" },
  [StatusTone.EXCEPTION]: { backgroundColor: "#b91c1c", color: "#ffffff" },
};

export enum StatusPillStyle {
  FLAT = "flat",
  // The earlier raised look, kept in case design switches back; no call site uses it.
  GLOSSY = "glossy",
}

// A soft drop shadow, a light top edge and a slight top-to-bottom shade.
const RAISED: CSSProperties = {
  backgroundImage: "linear-gradient(rgba(255,255,255,0.14), rgba(0,0,0,0.12))",
  boxShadow: "0 2px 4px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.25)",
};

export enum StatusPillOutline {
  NONE = "none",
  // A white edge that lifts the pill off a coloured bar, such as the orange page header.
  WHITE = "white",
}

// Border-box, so the pill keeps its height with the edge.
export const statusPillOutline = (outline: StatusPillOutline): CSSProperties =>
  outline === StatusPillOutline.WHITE ? { border: "1.5px solid #ffffff", boxSizing: "border-box" } : {};

export const statusPillColors = (tone: StatusTone, variant: StatusPillStyle): CSSProperties =>
  variant === StatusPillStyle.GLOSSY ? { ...GLOSSY_TONE_COLORS[tone], ...RAISED } : FLAT_TONE_COLORS[tone];
